import React, { useState } from 'react';
import { 
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  Link,
  Paper,
  TextField,
  Typography,
  CssBaseline,
  Fade,
  InputAdornment,
  IconButton,
  Divider,
  Snackbar,
  Alert,
} from '@mui/material';
import { 
  EmailRounded,
  LockRounded,
  Visibility,
  VisibilityOff,
  GitHub,
  Google,
  Facebook
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { motion } from 'framer-motion';
import { useNavigate } from "react-router-dom";
const GradientBox = styled(Box)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(3)
}));

const AuthPaper = styled(Paper)(({ theme }) => ({
  borderRadius: 24,
  padding: theme.spacing(6),
  boxShadow: theme.shadows[10],
  transition: 'transform 0.3s ease',
  '&:hover': {
    transform: 'translateY(-4px)'
  },
  maxWidth: 450,
  width: '100%'
}));

const SocialIcon = styled(IconButton)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 12,
  padding: theme.spacing(1.5),
  margin: theme.spacing(0, 1),
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    transform: 'scale(1.1)'
  }
}));

const LoginPage = () => {
    const [formData, setFormData] = useState({
        username: "",
        password: "",
      });
    
      const [showPassword, setShowPassword] = useState(false);
      const [loading, setLoading] = useState(false);
      const [openSnackbar, setOpenSnackbar] = useState(false); // Snackbar visibility state
      const [snackbarMessage, setSnackbarMessage] = useState(""); // Snackbar message state
      const [snackbarSeverity, setSnackbarSeverity] = useState("success"); // Severity for snackbar (success or error)
    
      const navigate = useNavigate(); // Initialize navigation
    
      const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
      };
    
      const handleCloseSnackbar = () => {
        setOpenSnackbar(false); // Close snackbar
      };
    
      const handleLogin = async () => {
        setLoading(true);
        
        setSnackbarMessage(""); // Reset snackbar message
        setSnackbarSeverity("success"); // Reset snackbar severity
    
        const requestBody = {
          ComID: "EH25",
          Username: formData.username,
          Password: formData.password, // Use user input
          NotificationToken: "NOT123",
          Source: "W",
          DeviceID: "NEW DEVICE",
        };
    
        try {
          const response = await fetch(
            "https://testing.esnep.com/magnessa/api/bo/login",
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify(requestBody),
            }
          );
    
          const data = await response.json();
    
         // Check if the StatusCode indicates a failure (non-200 status codes)
         if (data.StatusCode !== 200) {
          
          setSnackbarMessage(data.Message || "An error occurred.");
          setSnackbarSeverity("error"); // Set severity to "error"
          setOpenSnackbar(true); // Show snackbar
          return;
        }
    
          console.log("Login Successful:", data);
    
          sessionStorage.setItem("admin_user", JSON.stringify(data));
    
          // Retrieve the stored redirect path or go to the dashboard
          const redirectPath = sessionStorage.getItem("redirectAfterLogin") || "/admin-dashboard";

          sessionStorage.removeItem("redirectAfterLogin");
    
          // Show success message in snackbar
          setSnackbarMessage("Login Successful!");
          setSnackbarSeverity("success"); // Set severity to "success"
          setOpenSnackbar(true); // Show snackbar
    
          navigate(redirectPath, { replace: true });

    
        } catch (err) {
         
          setSnackbarMessage(err.message); // Show error message in snackbar
          setSnackbarSeverity("error"); // Set severity to "error"
          setOpenSnackbar(true); // Show snackbar
          console.error("Login Error:", err);
        } finally {
          setLoading(false);
        }

      };
    
      const handleSubmit = (e) => {
        e.preventDefault();
        handleLogin();
      };

  return (
    <>
    <GradientBox>
      <CssBaseline />
      <Fade in={true} timeout={800}>
        <AuthPaper>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Box textAlign="center" mb={4}>
              <Typography 
                variant="h4" 
                gutterBottom 
                sx={{ 
                  fontWeight: 700,
                  background: 'linear-gradient(45deg, #2b2d42 30%, #4a4e69 90%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}
              >
                Admin Login
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Please sign in to continue to your account
              </Typography>
            </Box>

            <form onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label="Username"
                variant="outlined"
                margin="normal"
                name="username"
                value={formData.username}
                onChange={handleChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailRounded color="action" />
                    </InputAdornment>
                  ),
                  style: { borderRadius: 12 }
                }}
              />

              <TextField
                fullWidth
                label="Password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                variant="outlined"
                margin="normal"
                value={formData.password}
                onChange={handleChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockRounded color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                  style: { borderRadius: 12 }
                }}
              />

              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                my: 2 
              }}>
                <FormControlLabel
                  control={<Checkbox color="primary" />}
                  label="Remember me"
                />
                <Link href="#" variant="body2" underline="hover">
                  Forgot password?
                </Link>
              </Box>

              <Button
                fullWidth
                variant="contained"
                size="large"
                type="submit"
                sx={{
                  mt: 2,
                  py: 1.5,
                  borderRadius: 12,
                  fontWeight: 600,
                  background: 'linear-gradient(45deg, #2b2d42 30%, #4a4e69 90%)',
                  '&:hover': {
                    transform: 'scale(1.02)',
                    boxShadow: 3
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                {loading ? "Logging in..." : "Login"}
              </Button>
            </form>

            <Divider sx={{ my: 3, color: 'text.secondary' }}>
              <Typography variant="body2">Follow us on</Typography>
            </Divider>

            <Box textAlign="center" mt={3}>
              <SocialIcon aria-label="Google">
                <Google sx={{ color: '#DB4437' }} />
              </SocialIcon>
              <SocialIcon aria-label="GitHub">
                <GitHub />
              </SocialIcon>
              <SocialIcon aria-label="Facebook">
                <Facebook sx={{ color: '#1877F2' }} />
              </SocialIcon>
            </Box>
          </motion.div>
        </AuthPaper>
      </Fade>
    </GradientBox>

       {/* Snackbar for messages */}
          <Snackbar
            open={openSnackbar}
            autoHideDuration={6000}
            onClose={handleCloseSnackbar}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
          >
            <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: "100%" }} >
              {snackbarMessage}
            </Alert>
          </Snackbar>
          </>
  );
};

export default LoginPage;